import { Box, Typography } from '@evaneos/b2b-ui';
import FolderIcon from '@evaneos/b2b-ui/icons/FolderCopyOutlined';
import { IntlShape, useIntl } from 'react-intl';

import { DossierId, OpenedRequests } from '../../../resources/dossier/types';
import { DefinitionList } from '../../../design-system/DefinitionList/DefinitionList';
import { useDossierLayoutInfo } from '../DossierLayoutInfoContext';

type OtherRequestInfoProps = {
    openedRequests: OpenedRequests[] | undefined;
    currentDossierId: DossierId;
};

function formatOtherRequestDisplay(
    request: OpenedRequests,
    formatDate: IntlShape['formatDate'],
    formatMessage: IntlShape['formatMessage'],
): string {
    const dateFormat = { day: 'numeric', month: 'short', year: 'numeric' } as const;
    const parts = [request.destination];

    if (request.arrivalDate) {
        parts.push(`(${formatDate(request.arrivalDate, dateFormat)})`);
    }

    parts.push(formatMessage({ id: `steps.${request.step}.name` }));

    if (request.requestDate) {
        parts.push(
            formatMessage(
                { id: 'dossier.information.multirequester.made_on_date' },
                {
                    date: formatDate(request.requestDate, dateFormat),
                },
            ),
        );
    }

    return parts.join(' - ');
}

export function OtherRequestInfo({ openedRequests, currentDossierId }: OtherRequestInfoProps) {
    const { formatDate, formatMessage } = useIntl();
    const { isFirstColumnNarrow } = useDossierLayoutInfo();

    const otherRequests = openedRequests
        ?.filter((request) => request.id !== currentDossierId)
        ?.sort((a, b) => {
            if (!a.requestDate || !b.requestDate) return 0;
            return new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime();
        });

    if (!otherRequests || otherRequests.length === 0) {
        return null;
    }

    return (
        <DefinitionList.Item>
            <DefinitionList.Label
                id="other-request"
                icon={<FolderIcon fontSize="small" aria-hidden="true" />}
                iconOnly={isFirstColumnNarrow}
            >
                {formatMessage(
                    { id: 'dossier.information.multirequester.other_requests' },
                    {
                        count: otherRequests.length,
                    },
                )}
            </DefinitionList.Label>
            <DefinitionList.StaticValue>
                <Box
                    component="ul"
                    sx={{
                        maxHeight: '140px',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 0,
                        gap: 1,
                    }}
                >
                    {otherRequests.map((request) => (
                        <Typography key={request.id} component="li" variant="body2">
                            {formatOtherRequestDisplay(request, formatDate, formatMessage)}
                        </Typography>
                    ))}
                </Box>
            </DefinitionList.StaticValue>
        </DefinitionList.Item>
    );
}
